import React, { ReactNode, Suspense } from 'react';
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from 'react-error-boundary';
import secureLocalStorage from 'react-secure-storage';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RouteFallback from '../../Components/Template/RouteFallback/RouteFallback';
import { lazyWithRetry } from '../lazyWithRetry';

// The error page is loaded on demand: importing it statically dragged the generic
// LabelInputField (and with it ~25 input components) into the eager startup bundle.
const ErrorHandling = lazyWithRetry(() => import('../../Pages/ErrorHandling/ErrorHandling'));

/** Last line of defence when even the error page's chunk cannot be loaded. */
const PlainErrorFallback: React.FC = () => {
    const { t } = useTranslation();
    return (
        <div className="route-fallback" role="alert">
            {t('common.load.failed')}
        </div>
    );
};

// The error page reads the stored error itself (secureLocalStorage 'error'), so the
// FallbackProps react-error-boundary passes in are intentionally not forwarded.
const LazyErrorFallback: React.FC<FallbackProps> = () => (
    <ReactErrorBoundary fallback={<PlainErrorFallback />}>
        <Suspense fallback={<RouteFallback />}>
            <ErrorHandling />
        </Suspense>
    </ReactErrorBoundary>
);

interface MyErrorBoundaryProps {
    children: ReactNode;
}

const MyErrorBoundary: React.FC<MyErrorBoundaryProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
        console.error("React error boundary", error, errorInfo);
        secureLocalStorage.setItem('error', errorInfo);
        navigate("/error-handling");
    };

    return (
        <ReactErrorBoundary
            key={location.pathname}
            FallbackComponent={LazyErrorFallback}
            onError={handleError}>
            {children}
        </ReactErrorBoundary>
    );
};

export default MyErrorBoundary;
